/* eslint-disable @atlaskit/volt-strict-mode/no-re-exports -- Use direct imports. */
import type { GasPayload } from '@atlaskit/analytics-gas-types';

// oxlint-disable typescript-eslint(consistent-type-imports)
// oxlint-disable-next-line typescript/consistent-type-imports
import { CONFIG_TASK_SUFFIXES } from './config-task-suffixes';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { CONFIG_TASK_SUFFIXES } from './config-task-suffixes';

/**
 * Attributes shape for analytics events.
 */
export type AnalyticsAttributes = {
	attributes: {
		commonAttributes?: Record<string, unknown>;
		additionalAttributes?: Record<string, unknown>;
	};
	nonPrivacySafeAttributes?: Record<string, unknown>;
};

/**
 * Analytics event payload type.
 * Combines GAS payload with our attribute structure.
 */
export type FireAnalyticsEvent = GasPayload & AnalyticsAttributes;

/**
 * Degradable dependency types for config fetching.
 */
export type DegradableDependency =
	| 'search-config'
	| 'search-3p-config'
	| 'outbound-auth-github'
	| 'search-combined-config';

/**
 * Cache request names for tracking cache access.
 */
export type CacheRequestName =
	| 'search-config'
	| 'search-3p-config'
	| 'outbound-auth-github'
	| 'search-combined-config';

export type ConfigTaskSuffix = (typeof CONFIG_TASK_SUFFIXES)[keyof typeof CONFIG_TASK_SUFFIXES];

/**
 * Builds a full task name from the surface prefix and task suffix.
 * @param surfacePrefix - e.g., 'search-page', 'quick-find', 'dynamic-cards'
 * @param suffix - one of CONFIG_TASK_SUFFIXES values
 */
export const buildTaskName = (surfacePrefix: string, suffix: ConfigTaskSuffix): string =>
	`${surfacePrefix}/${suffix}`;

/**
 * Required analytics configuration that consumers must provide.
 * This ensures all search surfaces fire consistent analytics.
 */
export interface ConfigFetchAnalytics {
	/**
	 * The search session ID for correlating events.
	 * Typically obtained from useSearchSessionId() in FPS.
	 */
	searchSessionId: string;

	/**
	 * The source of the analytics event (e.g., 'fullPageSearch', 'quickFind').
	 * Used in the event payload to identify where the event came from.
	 */
	source: string;

	/**
	 * The action subject for analytics events (e.g., 'searchPage', 'quickFind').
	 * This identifies the surface type in analytics.
	 */
	actionSubject: string;

	/**
	 * Prefix for task names in vital stats (e.g., 'search-page', 'quick-find').
	 * Full task name becomes: `${taskNamePrefix}/first-party-config-ux`
	 */
	taskNamePrefix: string;

	/**
	 * Function to fire analytics events.
	 * Typically the fireAnalyticsEvent from useAnalytics() in FPS.
	 */
	fireAnalyticsEvent: (event: FireAnalyticsEvent) => void;

	/**
	 * Function to log exceptions.
	 * Typically from useLogException() in FPS.
	 */
	logException: (error: Error) => void;
}

// ============================================================================
// Degraded Dependency Event
// ============================================================================

type OnDegradedDependencyArgs = {
	dependency: DegradableDependency;
	searchSessionId: string;
	reason: string;
	source: string;
	actionSubject: string;
};

/**
 * Fires an event when a dependency of search fails or times out.
 * Note: Should be fired at the request level, so the UX may not be affected due to caching.
 */
export const onDegradedDependency = ({
	dependency,
	searchSessionId,
	reason,
	source,
	actionSubject,
}: OnDegradedDependencyArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'degradedDependency',
		actionSubject,
		eventType: 'track',
		attributes: {
			additionalAttributes: {
				degradedDependency: dependency,
				reason,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Failed Dependency Event
// ============================================================================

type OnFailedDependencyArgs = {
	dependency: DegradableDependency;
	searchSessionId: string;
	source: string;
	actionSubject: string;
};

/**
 * Fires an event when a dependency of search completely fails.
 */
export const onFailedDependency = ({
	dependency,
	searchSessionId,
	source,
	actionSubject,
}: OnFailedDependencyArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'failedDependency',
		actionSubject,
		eventType: 'operational',
		attributes: {
			additionalAttributes: {
				failedDependency: dependency,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Cache Access Event
// ============================================================================

type OnCacheAccessArgs = {
	requestName: CacheRequestName;
	searchSessionId: string;
	cacheHit: boolean;
	refreshTriggered: boolean;
	source: string;
	actionSubject: string;
};

/**
 * Records the hit/miss rate of request caching.
 */
export const onRequestCacheAccess = ({
	requestName,
	searchSessionId,
	cacheHit,
	refreshTriggered,
	source,
	actionSubject,
}: OnCacheAccessArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'requestCacheAccess',
		actionSubject,
		eventType: 'track',
		attributes: {
			additionalAttributes: {
				requestName,
				cacheHit,
				refreshTriggered,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Vital Stats Events (Task lifecycle tracking)
// ============================================================================

type MoreContext = Record<string, unknown>;

const baseVitalStatsEvent = ({
	searchSessionId,
	taskId,
	task,
	action,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: string;
	action: 'taskStart' | 'taskSuccess' | 'taskFail' | 'taskAbort';
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return {
		action,
		actionSubject: 'ui',
		eventType: 'operational',
		attributes: {
			additionalAttributes: {
				task,
				taskId,
				hostname: typeof window !== 'undefined' ? window.location.hostname : '',
				searchSessionId,
				...moreContext,
			},
		},
	};
};

type TaskEventArgs = {
	searchSessionId: string;
	taskId: string;
	/**
	 * Full task name, e.g., 'search-page/first-party-config-ux'.
	 * Use buildTaskName(prefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_UX) to construct.
	 */
	task: string;
	moreContext?: MoreContext;
};

/**
 * Fires when a task starts.
 */
export const onTaskStart = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskStart', moreContext });
};

/**
 * Fires when a task completes successfully.
 */
export const onTaskSuccess = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskSuccess', moreContext });
};

/**
 * Fires when a task fails.
 */
export const onTaskFail = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskFail', moreContext });
};

/**
 * Fires when a task is aborted (e.g., superseded or component unmounted).
 */
export const onTaskAbort = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskAbort', moreContext });
};
